import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import { useDispatch, useSelector } from "react-redux";
import PageHeader from "../../../../components/common/PageHeader";
import StatusChip from "../../../../components/common/StatusChip";
import ActionIconButton from "../../../../components/common/ActionIconButton";
import PageLoader from "../../../../components/common/PageLoader";
import ChipList from "../../../../components/common/ChipList";
import TruncatedText from "../../../../components/common/TruncatedText";
import EmptyState from "../../../../components/common/EmptyState";
import SearchFilterBar from "../../../../components/common/SearchFilterBar";
import DataTable from "../../../../components/DataTable";
import { appActions, tenantActions } from "../../../../redux/actions";
import { setTenantId } from "../../../../utils/requestHeaders";
import useAuth from "../../../../hooks/useAuth";
import { getApiErrorMessage } from "../../../../helpers";
import { getTenantClientId } from "../../../../config/loginTenants";
import {
  DEFAULT_PAGE_SIZE,
  USER_TYPES,
  USER_TYPE_LABELS,
  USER_TYPE_SUBTITLES,
} from "../../constants";
import {
  systemUserService,
  tenantUserService,
  roleService,
} from "../../services";
import { tenantRoleService } from "../../../role-management/services";
import { normalizeRoleList } from "../../../role-management/utils/normalizeRole";
import {
  matchesUserSearch,
  normalizeUser,
  normalizeUserList,
} from "../../utils/normalizeUser";
import ChangePasswordDialog from "../ChangePasswordDialog";
import UpdateRoleDialog from "../UpdateRoleDialog";
import UserDetailsDrawer from "../UserDetailsDrawer";
import SystemUserFormDialog from "../SystemUserFormDialog";
import TenantUserFormDialog from "../TenantUserFormDialog";

const UserManagement = ({ userType = USER_TYPES.SYSTEM }) => {
  const dispatch = useDispatch();
  const { tenantId: authTenantId, activeTenantId } = useAuth();
  const fallbackTenantId = useSelector(
    (state) => state?.tenantReducer?.tenantInfo?.tenantId
  );
  const tenantId = activeTenantId || authTenantId || fallbackTenantId;

  const isSystem = userType === USER_TYPES.SYSTEM;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [hasMore, setHasMore] = useState(false);

  const [usernameFilter, setUsernameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [mobileFilter, setMobileFilter] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    username: "",
    email: "",
    mobile: "",
  });

  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [roleLoading, setRoleLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [userFormOpen, setUserFormOpen] = useState(false);
  const [userFormMode, setUserFormMode] = useState("create");
  const [userFormLoading, setUserFormLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const ensureTenantHeader = useCallback(() => {
    if (!isSystem && tenantId) {
      setTenantId(tenantId);
    }
  }, [isSystem, tenantId]);

  const loadUsers = useCallback(
    async (index = 0, filters = appliedFilters, size = pageSize) => {
      setLoading(true);
      setError("");
      ensureTenantHeader();

      try {
        let payload;

        if (isSystem) {
          payload = await systemUserService.getAllSystemUsers();
          const normalized = normalizeUserList(payload);
          const filtered = normalized.filter((user) =>
            matchesUserSearch(user, filters)
          );
          setUsers(filtered);
          setHasMore(false);
        } else {
          const hasSearch =
            Boolean(filters.username) ||
            Boolean(filters.email) ||
            Boolean(filters.mobile);

          payload = hasSearch
            ? await tenantUserService.searchTenantUsers({
                size,
                index,
                tenantId,
                ...filters,
              })
            : await tenantUserService.getAllTenantUsers({
                size,
                index,
                tenantId,
              });

          const normalized = normalizeUserList(payload);
          const filtered = normalized.filter((user) =>
            matchesUserSearch(user, filters)
          );
          setUsers(filtered);
          setHasMore(normalized.length >= size);
        }

        setPageIndex(index);
        setPageSize(size);
      } catch (err) {
        const message = getApiErrorMessage(err);
        setUsers([]);
        setError(message);
        dispatch(appActions.apiErrorMessage(message));
      } finally {
        setLoading(false);
      }
    },
    [appliedFilters, dispatch, ensureTenantHeader, isSystem, pageSize, tenantId]
  );

  useEffect(() => {
    if (!authTenantId) {
      dispatch(tenantActions.getTenantInfo());
    }
  }, [authTenantId, dispatch]);

  useEffect(() => {
    loadUsers(0, appliedFilters);
  }, [appliedFilters, isSystem, tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the user clears all filter inputs (e.g. backspace), reset applied
  // search so the full list loads again without requiring Clear.
  useEffect(() => {
    const draftEmpty =
      !usernameFilter.trim() && !emailFilter.trim() && !mobileFilter.trim();
    const hasAppliedFilter =
      Boolean(appliedFilters.username) ||
      Boolean(appliedFilters.email) ||
      Boolean(appliedFilters.mobile);

    if (draftEmpty && hasAppliedFilter) {
      setAppliedFilters({ username: "", email: "", mobile: "" });
    }
  }, [
    usernameFilter,
    emailFilter,
    mobileFilter,
    appliedFilters.username,
    appliedFilters.email,
    appliedFilters.mobile,
  ]);

  const handleSearch = () => {
    setAppliedFilters({
      username: usernameFilter.trim(),
      email: emailFilter.trim(),
      mobile: mobileFilter.trim(),
    });
  };

  const handleClearSearch = () => {
    setUsernameFilter("");
    setEmailFilter("");
    setMobileFilter("");
    setAppliedFilters({ username: "", email: "", mobile: "" });
  };

  const openDetails = useCallback(
    async (row) => {
      setSelectedUser(row);
      setDetailsOpen(true);
      ensureTenantHeader();

      try {
        const userId = row.userId || row.id;
        const detail = isSystem
          ? await systemUserService.getSystemUserById(userId)
          : await tenantUserService.getTenantUserById(userId, tenantId);
        setSelectedUser(normalizeUser({ ...row, ...(detail || {}) }));
      } catch (err) {
        dispatch(appActions.apiErrorMessage(getApiErrorMessage(err)));
      }
    },
    [dispatch, ensureTenantHeader, isSystem, tenantId]
  );

  const handleChangePassword = async (newPassword) => {
    if (!selectedUser?.userId) return;
    setPasswordLoading(true);
    ensureTenantHeader();

    try {
      if (isSystem) {
        await systemUserService.changeSystemUserPassword(
          selectedUser.userId,
          newPassword
        );
      } else {
        await tenantUserService.changeTenantUserPassword(
          selectedUser.userId,
          newPassword,
          tenantId
        );
      }
      dispatch(appActions.apiSuccessMessage("Password updated successfully."));
      setPasswordOpen(false);
    } catch (err) {
      dispatch(appActions.apiErrorMessage(getApiErrorMessage(err)));
    } finally {
      setPasswordLoading(false);
    }
  };

  const loadRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      if (isSystem) {
        setRoles(await roleService.getAllRoles());
      } else {
        const payload = await tenantRoleService.getAllTenantRoles(tenantId);
        setRoles(
          normalizeRoleList(payload)
            .map((role) => role.name)
            .filter(Boolean)
        );
      }
    } catch {
      setRoles([]);
    } finally {
      setRolesLoading(false);
    }
  }, [isSystem, tenantId]);

  const openCreateUser = async () => {
    setUserFormMode("create");
    setSelectedUser(null);
    setUserFormOpen(true);
    await loadRoles();
  };

  const openEditUser = useCallback(async (row = selectedUser) => {
    if (!isSystem || !row) return;
    setSelectedUser(row);
    setUserFormMode("edit");
    setUserFormOpen(true);
    await loadRoles();
  }, [isSystem, loadRoles, selectedUser]);

  const handleToggleTenantMfa = async (mfaEnabled) => {
    if (!selectedUser?.userId || isSystem) return;
    setSaving(true);
    try {
      await tenantUserService.updateTenantUser(
        selectedUser.userId,
        { mfaEnabled },
        tenantId
      );
      const updated = normalizeUser({ ...selectedUser, mfaEnabled });
      setSelectedUser(updated);
      setUsers((prev) =>
        prev.map((item) =>
          item.userId === updated.userId ? { ...item, mfaEnabled } : item
        )
      );
      dispatch(appActions.apiSuccessMessage("MFA updated successfully."));
    } catch (err) {
      dispatch(appActions.apiErrorMessage(getApiErrorMessage(err)));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSystemUser = async (payload) => {
    setUserFormLoading(true);
    try {
      if (userFormMode === "create") {
        await systemUserService.createSystemUser(payload);
        dispatch(appActions.apiSuccessMessage("System user created successfully."));
      } else {
        const userId = selectedUser?.userId;
        if (!userId) return;
        await systemUserService.updateSystemUser(userId, payload);
        dispatch(appActions.apiSuccessMessage("System user updated successfully."));
      }
      setUserFormOpen(false);
      setDetailsOpen(false);
      await loadUsers(isSystem ? 0 : pageIndex, appliedFilters);
    } catch (err) {
      throw new Error(getApiErrorMessage(err));
    } finally {
      setUserFormLoading(false);
    }
  };

  const handleSaveTenantUser = async (payload) => {
    setUserFormLoading(true);
    try {
      const clientId = getTenantClientId(tenantId);
      if (!clientId) {
        throw new Error("Tenant client id is missing.");
      }
      await tenantUserService.createTenantUser(
        { ...payload, clientId },
        tenantId
      );
      dispatch(appActions.apiSuccessMessage("User created successfully."));
      setUserFormOpen(false);
      setDetailsOpen(false);
      await loadUsers(pageIndex, appliedFilters);
    } catch (err) {
      throw new Error(getApiErrorMessage(err));
    } finally {
      setUserFormLoading(false);
    }
  };

  const openDelete = useCallback((row = selectedUser) => {
    if (!row) return;
    setSelectedUser(normalizeUser(row));
    setDetailsOpen(false);
    setDeleteOpen(true);
  }, [selectedUser]);

  const handleDeleteTenantUser = async () => {
    if (!selectedUser?.userId) return;
    setSaving(true);
    try {
      await tenantUserService.deleteTenantUser(selectedUser.userId, tenantId);
      dispatch(appActions.apiSuccessMessage("User deleted successfully."));
      setDeleteOpen(false);
      setDetailsOpen(false);
      await loadUsers(pageIndex, appliedFilters);
    } catch (err) {
      dispatch(appActions.apiErrorMessage(getApiErrorMessage(err)));
    } finally {
      setSaving(false);
    }
  };

  const handleUnlockTenantUser = async (row = selectedUser) => {
    if (!row?.userId) return;
    setSaving(true);
    try {
      await tenantUserService.updateTenantUserStatus(
        row.userId,
        "unlock",
        tenantId
      );
      dispatch(appActions.apiSuccessMessage("User unlocked successfully."));
      setDetailsOpen(false);
      await loadUsers(pageIndex, appliedFilters);
    } catch (err) {
      dispatch(appActions.apiErrorMessage(getApiErrorMessage(err)));
    } finally {
      setSaving(false);
    }
  };

  const openRoleDialog = async () => {
    setRoleOpen(true);
    await loadRoles();
  };

  const handleUpdateRole = async (nextRoles) => {
    if (!selectedUser?.userId) return;
    setRoleLoading(true);
    ensureTenantHeader();

    try {
      if (isSystem) {
        await systemUserService.updateSystemUserRole(
          selectedUser.userId,
          nextRoles
        );
      } else {
        await tenantUserService.updateTenantUserRole(
          selectedUser.userId,
          nextRoles,
          tenantId
        );
      }
      const updated = { ...selectedUser, roles: nextRoles };
      setSelectedUser(updated);
      setUsers((prev) =>
        prev.map((user) =>
          user.userId === updated.userId ? { ...user, roles: nextRoles } : user
        )
      );
      dispatch(appActions.apiSuccessMessage("Roles updated successfully."));
      setRoleOpen(false);
    } catch (err) {
      dispatch(appActions.apiErrorMessage(getApiErrorMessage(err)));
    } finally {
      setRoleLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "userId",
        headerName: "User ID",
        flex: 1.1,
        minWidth: 160,
        renderCell: (params) => <TruncatedText value={params.value} />,
      },
      {
        field: "username",
        headerName: "Username",
        flex: 1,
        minWidth: 140,
        renderCell: (params) => <TruncatedText value={params.value} />,
      },
      {
        field: "firstname",
        headerName: "First Name",
        flex: 0.8,
        minWidth: 120,
        renderCell: (params) => <TruncatedText value={params.value} />,
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1.1,
        minWidth: 160,
        renderCell: (params) => <TruncatedText value={params.value} />,
      },
      {
        field: "mobile",
        headerName: "Mobile",
        flex: 0.9,
        minWidth: 120,
        renderCell: (params) => <TruncatedText value={params.value} />,
      },
      {
        field: "roles",
        headerName: "Roles",
        flex: 1.2,
        minWidth: 180,
        sortable: false,
        valueGetter: (value, row) =>
          Array.isArray(row?.roles) ? row.roles.join(", ") : "",
        renderCell: (params) => (
          <ChipList items={params.row.roles} maxVisible={1} />
        ),
      },
      {
        field: "enabled",
        headerName: "Status",
        flex: 0.7,
        minWidth: 110,
        type: "string",
        valueGetter: (value, row) => {
          if (row?.locked) return "Locked";
          return row?.enabled ? "Active" : "Disabled";
        },
        renderCell: (params) => (
          <StatusChip enabled={params.row.enabled} locked={params.row.locked} />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: isSystem ? 120 : 156,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <ActionIconButton
              title="View Details"
              icon={<VisibilityOutlinedIcon fontSize="small" />}
              onClick={() => openDetails(params.row)}
            />
            {isSystem ? (
              <ActionIconButton
                title="Edit User"
                icon={<EditOutlinedIcon fontSize="small" />}
                onClick={() => openEditUser(params.row)}
              />
            ) : null}
            {!isSystem && params.row.locked ? (
              <ActionIconButton
                title="Unlock User"
                icon={<LockOpenOutlinedIcon fontSize="small" />}
                onClick={() => handleUnlockTenantUser(params.row)}
              />
            ) : null}
            {!isSystem ? (
              <ActionIconButton
                title="Delete User"
                icon={<DeleteOutlineOutlinedIcon fontSize="small" />}
                onClick={() => openDelete(params.row)}
              />
            ) : null}
          </Stack>
        ),
      },
    ],
    [isSystem, openDelete, openDetails, openEditUser]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
        sx={{ mb: 1 }}
      >
        <PageHeader
          title={USER_TYPE_LABELS[userType] || "Users"}
          subtitle={USER_TYPE_SUBTITLES[userType]}
          sx={{ mb: 0 }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<PersonAddAltOutlinedIcon />}
            onClick={openCreateUser}
          >
            Add User
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            onClick={() => loadUsers(pageIndex, appliedFilters)}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <SearchFilterBar
        fields={[
          {
            key: "username",
            label: "Username",
            value: usernameFilter,
            onChange: (e) => setUsernameFilter(e.target.value),
            width: 280,
          },
          ...(!isSystem
            ? [
                {
                  key: "email",
                  label: "Email",
                  value: emailFilter,
                  onChange: (e) => setEmailFilter(e.target.value),
                  width: 260,
                },
                {
                  key: "mobile",
                  label: "Mobile",
                  value: mobileFilter,
                  onChange: (e) => setMobileFilter(e.target.value),
                  width: 200,
                },
              ]
            : []),
        ]}
        onSearch={handleSearch}
        onClear={handleClearSearch}
        helperText={
          isSystem
            ? "System users are filtered client-side until a dedicated search API is available."
            : ""
        }
      />

      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          minHeight: 360,
        }}
      >
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading && !users.length ? (
          <PageLoader text="Loading users..." />
        ) : !loading && !users.length ? (
          <EmptyState
            title="No users found"
            description="No users found for the current filters."
          />
        ) : (
          <>
            <DataTable
              rows={users}
              columns={columns}
              pageSize={pageSize}
              showPagination
              loading={loading}
              autoHeight
              getRowId={(row) => row.userId || row.id}
              paginationMode={isSystem ? "client" : "server"}
              rowCount={
                isSystem
                  ? users.length
                  : hasMore
                    ? (pageIndex + 1) * pageSize + 1
                    : pageIndex * pageSize + users.length
              }
              paginationModel={
                isSystem
                  ? undefined
                  : { page: pageIndex, pageSize }
              }
              onPaginationModelChange={
                isSystem
                  ? undefined
                  : ({ page, pageSize: nextSize }) => {
                      loadUsers(page, appliedFilters, nextSize);
                    }
              }
            />
          </>
        )}
      </Paper>

      <UserDetailsDrawer
        open={detailsOpen}
        user={selectedUser}
        onClose={() => setDetailsOpen(false)}
        onChangePassword={() => setPasswordOpen(true)}
        onUpdateRole={openRoleDialog}
        onEdit={
          isSystem
            ? () => {
                setDetailsOpen(false);
                openEditUser(selectedUser);
              }
            : undefined
        }
        onToggleMfa={!isSystem ? handleToggleTenantMfa : undefined}
        mfaSaving={!isSystem && saving}
        onDelete={!isSystem ? () => openDelete(selectedUser) : undefined}
        onUnlock={
          !isSystem && selectedUser?.locked
            ? () => handleUnlockTenantUser(selectedUser)
            : undefined
        }
      />

      {isSystem ? (
        <SystemUserFormDialog
          open={userFormOpen}
          mode={userFormMode}
          user={selectedUser}
          roles={roles}
          rolesLoading={rolesLoading}
          loading={userFormLoading}
          onClose={() => setUserFormOpen(false)}
          onSubmit={handleSaveSystemUser}
        />
      ) : (
        <TenantUserFormDialog
          open={userFormOpen}
          user={selectedUser}
          roles={roles}
          rolesLoading={rolesLoading}
          loading={userFormLoading}
          onClose={() => setUserFormOpen(false)}
          onSubmit={handleSaveTenantUser}
        />
      )}

      <ChangePasswordDialog
        open={passwordOpen}
        user={selectedUser}
        loading={passwordLoading}
        onClose={() => setPasswordOpen(false)}
        onSubmit={handleChangePassword}
      />

      <UpdateRoleDialog
        open={roleOpen}
        user={selectedUser}
        roles={roles}
        loading={roleLoading}
        rolesLoading={rolesLoading}
        onClose={() => setRoleOpen(false)}
        onSubmit={handleUpdateRole}
      />

      <Dialog
        open={deleteOpen}
        onClose={saving ? undefined : () => setDeleteOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete user{" "}
            <strong>{selectedUser?.username || selectedUser?.userId}</strong>? This
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteTenantUser}
            disabled={saving}
          >
            {saving ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
